// ============================================================
// DockerCheatSheet.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: "The DevOps Bible."
// - Focus: Commands, Architecture, and Best Practices.
// - UI: High-density Grid for fast scanning.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Stack,
    Divider,
    Chip,
    styled,
} from "@mui/material";

/* Icons */
import TerminalIcon from "@mui/icons-material/Terminal";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import HubIcon from "@mui/icons-material/Hub";
import ConstructionIcon from "@mui/icons-material/Construction";

/* ── Brand tokens ────────────────────────────────────────── */
const GOLD = "#E5C95F";
const NAVY_DEEP = "#100720";
const TEXT_WHITE = "#FFFFFF";
const TEXT_MUTED = "rgba(255, 255, 255, 0.7)";
const CODE_BG = "#011627";

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_DEEP,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    padding: theme.spacing(10, 2),
}));

const CheatCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid rgba(229, 201, 95, 0.2)`,
    borderRadius: "12px",
    height: "100%",
}));

const CodeBox = styled(Box)(({ theme }) => ({
    backgroundColor: CODE_BG,
    padding: theme.spacing(1.5),
    borderRadius: "6px",
    fontFamily: "'Fira Code', monospace",
    fontSize: "0.85rem",
    color: "#ADDB67", // Neon Green
    marginTop: theme.spacing(1),
    border: "1px solid rgba(173, 219, 103, 0.1)",
    whiteSpace: "pre-wrap",
}));

export default function DockerCheatSheet() {
    const sections = [
        {
            title: "Container Lifecycle",
            icon: <TerminalIcon sx={{ color: GOLD }} />,
            commands: [
                { cmd: "docker run -d --name app nginx", desc: "Run container in detached mode" },
                { cmd: "docker ps -a", desc: "List all containers (active & stopped)" },
                { cmd: "docker exec -it app sh", desc: "Open terminal inside container" },
                { cmd: "docker logs -f app", desc: "Follow container output logs" },
                { cmd: "docker stop/start app", desc: "Manage container state" },
                { cmd: "docker rm -f $(docker ps -aq)", desc: "Delete all containers" }
            ]
        },
        {
            title: "Images & Dockerfile",
            icon: <ConstructionIcon sx={{ color: GOLD }} />,
            commands: [
                { cmd: "docker build -t user/app:v1 .", desc: "Build image from local Dockerfile" },
                { cmd: "docker images", desc: "List local images" },
                { cmd: "docker rmi image_id", desc: "Remove an image" },
                { cmd: "docker system prune", desc: "Clean up unused data/layers" },
                { cmd: "docker tag old_name new_name", desc: "Rename/Version an image" }
            ]
        },
        {
            title: "Networking",
            icon: <SettingsEthernetIcon sx={{ color: GOLD }} />,
            commands: [
                { cmd: "docker network ls", desc: "List all virtual networks" },
                { cmd: "docker network create my-net", desc: "Create a custom bridge network" },
                { cmd: "docker run --network my-net ...", desc: "Connect container to network" },
                { cmd: "docker port app", desc: "Show port mappings" }
            ]
        },
        {
            title: "Volumes & Storage",
            icon: <StorageIcon sx={{ color: GOLD }} />,
            commands: [
                { cmd: "docker volume ls", desc: "List all named volumes" },
                { cmd: "docker volume create data", desc: "Create a persistent volume" },
                { cmd: "docker run -v data:/app/db ...", desc: "Mount named volume" },
                { cmd: "docker run -v $(pwd):/app ...", desc: "Bind mount (Dev mode)" }
            ]
        },
        {
            title: "Docker Compose",
            icon: <HubIcon sx={{ color: GOLD }} />,
            commands: [
                { cmd: "docker-compose up -d", desc: "Start entire stack in background" },
                { cmd: "docker-compose down", desc: "Stop & remove containers/networks" },
                { cmd: "docker-compose logs -f", desc: "View logs from all services" },
                { cmd: "docker-compose build --no-cache", desc: "Force rebuild of services" }
            ]
        }
    ];

    return (
        <PageWrapper>
            <Container maxWidth="lg">
                <Box sx={{ mb: 8, textAlign: "center" }}>
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2 }}>
                        Docker <span style={{ color: GOLD }}>Cheat Sheet</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: TEXT_MUTED }}>
                        ProDev Consult · Validated Engineering Reference
                    </Typography>
                    <Divider sx={{ mt: 4, bgcolor: "rgba(255,255,255,0.1)", width: "200px", mx: "auto" }} />
                </Box>

                <Grid container spacing={3}>
                    {sections.map((section, idx) => (
                        <Grid item xs={12} md={6} key={idx}>
                            <CheatCard>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                    {section.icon}
                                    <Typography variant="h6" fontWeight={800}>{section.title}</Typography>
                                </Stack>
                                {section.commands.map((c, i) => (
                                    <Box key={i} sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 700 }}>
                                            {c.desc}
                                        </Typography>
                                        <CodeBox>{c.cmd}</CodeBox>
                                    </Box>
                                ))}
                            </CheatCard>
                        </Grid>
                    ))}

                    {/* Best Practices Section */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 4, bgcolor: "rgba(229, 201, 95, 0.05)", border: `2px dashed ${GOLD}`, borderRadius: 4 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                <SecurityIcon sx={{ color: GOLD }} />
                                <Typography variant="h5" fontWeight={900}>Engineering Standards</Typography>
                            </Stack>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" color={GOLD}>OPTIMIZATION</Typography>
                                    <Typography variant="body2" color={TEXT_MUTED}>Use Alpine base images. Leverage multi-stage builds. Order Dockerfile layers from static to dynamic.</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" color={GOLD}>SECURITY</Typography>
                                    <Typography variant="body2" color={TEXT_MUTED}>Never run as root (use USER). Scan images for vulnerabilities. Use .dockerignore for secrets.</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" color={GOLD}>INTEGRITY</Typography>
                                    <Typography variant="body2" color={TEXT_MUTED}>Always version tags (avoid :latest in production). Use Read-Only filesystems where possible.</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 10, textAlign: "center" }}>
                    <Chip label="FINNISH ENGINEERING STANDARDS" sx={{ bgcolor: "#003580", color: "#fff", fontWeight: 900, px: 2 }} />
                </Box>
            </Container>
        </PageWrapper>
    );
}
